'use client';

import React from 'react';
import type {ReactNode} from 'react';
import {useState, useRef} from 'react';
import styles from './Popover.module.scss';
import {Align, Overlay} from '../../app/common.types';

interface PopoverProps {
  children?: ReactNode;
  trigger?: ReactNode;
  overlay: Overlay;
  align: Align;
  customContentClass?: string;
}

const Popover = (props: PopoverProps) => {
  const {children, trigger, overlay, align, customContentClass} = props;

  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = () => {
    if (isOpen) {
      document.removeEventListener('click', handleCloseOutside);
      document.removeEventListener('scroll', handleCloseOutside);
      document.removeEventListener('keydown', handleEscape);
    } else {
      document.addEventListener('click', handleCloseOutside);
      document.addEventListener('scroll', handleCloseOutside);
      document.addEventListener('keydown', handleEscape);
    }
    setIsOpen(!isOpen);
  };

  function isElement(value: unknown): value is HTMLElement {
    return value instanceof HTMLElement;
  }

  const handleCloseOutside = (event: Event) => {
    const clickingPopoverElement =
      popoverRef !== null &&
      isElement(popoverRef.current) &&
      popoverRef.current.contains(event.target as HTMLElement);
    const clickingTriggerElement =
      triggerRef !== null &&
      isElement(triggerRef.current) &&
      triggerRef.current?.contains(event.target as HTMLElement);
    if (!clickingPopoverElement && !clickingTriggerElement) {
      setIsOpen(false);
    }
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCloseOutside(event);
    }
  };

  const getClasses = (): string => {
    const base = [styles.content];

    if (customContentClass) {
      base.push(customContentClass);
    }

    if (overlay === Overlay.ALIGNBELOW) {
      base.push(styles.bottom);
    }
    if (align === Align.START) {
      base.push(styles.left);
    }
    if (align === Align.END) {
      base.push(styles.right);
    }
    if (align === Align.START) {
      base.push(styles.above);
    }
    return base.join(' ');
  };

  return (
    <div
      {...props}
      ref={triggerRef}
      className={styles.popover}
      onClick={handleToggle}
    >
      <span className={styles.trigger}>{trigger}</span>
      {isOpen && (
        <div
          ref={popoverRef}
          className={getClasses()}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export {Popover};
